import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { BiChevronLeft } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCategories,
  selectProducts,
} from "../store/slices/products/slice";
import { editProduct, getProduct } from "../store/slices/products/asyncThunks";
import { useNavigate, useParams } from "react-router-dom";
import AdminContainer from "../components/AdminContainer";
import AdminTitle from "../components/utils/AdminTitle";
import ProductForm from "../components/admin/ProductForm";
import AdminToast from "../components/utils/AdminToast";
import productsService from "../api/services/products";
import { FRONT_URL } from "../utils/constants";

const EditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const categories = useSelector(selectCategories);
  const products = useSelector(selectProducts);
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    name: "",
    price: "",
    salePrice: "",
    description: "",
    stockS: "",
    stockM: "",
    stockL: "",
    stockXL: "",
    stockSM: "",
    stockLXL: "",
    stockSocksS: "",
    stockSocksM: "",
    color: "",
    colorString: "",
    category: "",
    isInStock: true,
    badge: "",
    comingSoon: false,
  });
  const [originalForm, setOriginalForm] = useState(null);
  const [toast, setToast] = useState(null);

  const [existingPhotos, setExistingPhotos] = useState([]);
  const [thumbnailName, setThumbnailName] = useState(null);
  const [newThumbnail, setNewThumbnail] = useState(null); // { file, url }
  const [newImages, setNewImages] = useState([]); // [{ id, file, url }]
  useEffect(() => {
    dispatch(getProduct(+id))
      .unwrap()
      .then((res) => {
        const fetchedProduct = {
          name: res.data.name,
          description: res.data.description,
          price: res.data.price,
          salePrice: res.data.salePrice,
          stockS: res.data.stockS ?? 0,
          stockM: res.data.stockM ?? 0,
          stockL: res.data.stockL ?? 0,
          stockXL: res.data.stockXL ?? 0,
          stockSM: res.data.stockSM ?? 0,
          stockLXL: res.data.stockLXL ?? 0,
          stockSocksS: res.data.stockSocksS ?? 0,
          stockSocksM: res.data.stockSocksM ?? 0,
          color: res.data.color,
          colorString: res.data.colorString,
          badge: res.data.badge,
          isInStock: res.data.isInStock,
          comingSoon: res.data.comingSoon || false,
          category: res.data.categories[0].name,
        };

        setForm(fetchedProduct);
        setOriginalForm(fetchedProduct);
        setExistingPhotos(res.data.photos || []);
        setThumbnailName(res.data.thumbnailImage);
      })
      .catch((err) => console.log(err.message));
  }, [dispatch, id]);

  const removeNewImage = (imgId) => {
    setNewImages((prev) => prev.filter((i) => i.id !== imgId));
  };

  const handleDeleteExistingPhoto = async (photoId) => {
    try {
      await productsService.deletePhoto(id, photoId);
      setExistingPhotos((prev) => prev.filter((p) => p.id !== photoId));
      setToast({ type: "success", message: "Fotografija je obrisana." });
    } catch (err) {
      setToast({ type: "error", message: err.message });
    }
  };

  const handleInput = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      name: "",
      price: "",
      salePrice: "",
      description: "",
      stockS: "",
      stockM: "",
      stockL: "",
      stockXL: "",
      stockSM: "",
      stockLXL: "",
      stockSocksS: "",
      stockSocksM: "",
      color: "",
      colorString: "",
      category: "",
      isInStock: true,
      badge: "",
      comingSoon: false,
    });
  };

  const handleEditProduct = () => {
    if (!originalForm) return;

    if (!String(form.name || "").trim()) {
      setToast({ type: "error", message: "Naziv proizvoda ne sme biti prazan." });
      return;
    }
    if (!form.comingSoon && (!form.price || isNaN(form.price))) {
      setToast({ type: "error", message: "Unesite ispravnu cenu proizvoda." });
      return;
    }
    if (
      form.salePrice !== "" &&
      form.salePrice !== null &&
      form.salePrice !== undefined &&
      (isNaN(form.salePrice) || Number(form.salePrice) >= Number(form.price))
    ) {
      setToast({
        type: "error",
        message: "Akcijska cena mora biti broj niži od redovne cene.",
      });
      return;
    }
    if (!String(form.description || "").trim()) {
      setToast({ type: "error", message: "Opis proizvoda ne sme biti prazan." });
      return;
    }
    if (!String(form.colorString || "").trim()) {
      setToast({ type: "error", message: "Unesite naziv boje." });
      return;
    }
    if (!String(form.color || "").trim()) {
      setToast({ type: "error", message: "Unesite boju (hex kod)." });
      return;
    }
    if (!String(form.category || "").trim()) {
      setToast({ type: "error", message: "Izaberite kategoriju." });
      return;
    }

    const isChanged = Object.entries(form).some(
      ([key, value]) => value !== originalForm[key]
    );

    const hasImageChanges = !!newThumbnail || newImages.length > 0;

    if (!isChanged && !hasImageChanges) {
      setToast({ type: "error", message: "Nema izmena za čuvanje." });
      return;
    }

    dispatch(
      editProduct({
        id,
        name: form.name,
        description: form.description,
        price: form.price,
        salePrice: form.salePrice,
        color: form.color,
        colorString: form.colorString,
        badge: form.badge,
        stockS: form.stockS,
        stockM: form.stockM,
        stockL: form.stockL,
        stockXL: form.stockXL,
        stockSM: form.stockSM,
        stockLXL: form.stockLXL,
        stockSocksS: form.stockSocksS,
        stockSocksM: form.stockSocksM,
        category: form.category,
        isInStock: form.isInStock,
        comingSoon: form.comingSoon,
        thumbnailFile: newThumbnail?.file,
        imagesFiles: newImages.map((i) => i.file),
      })
    )
      .unwrap()
      .then(() => {
        setToast({ type: "success", message: "Izmene su sačuvane." });
        resetForm();
        setTimeout(() => navigate("/kontrolna-tabla"), 900);
      })
      .catch((err) =>
        setToast({
          type: "error",
          message: err.message || "Greška pri čuvanju izmena.",
        })
      );
  };

  return (
    <AdminContainer>
      {toast && (
        <AdminToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <PageHead>
        <AdminTitle kanji="改">Izmena proizvoda</AdminTitle>
        <BackBtn
          onClick={() => {
            navigate("/kontrolna-tabla");
          }}
        >
          <BiChevronLeft />
          Nazad
        </BackBtn>
      </PageHead>
      <ProductForm
        form={form}
        onInput={handleInput}
        categories={categories.data.map((c) => ({
          value: c.name,
          label: c.name,
        }))}
        thumbnailUrl={
          newThumbnail?.url ||
          (thumbnailName ? `${FRONT_URL}/thumbnails/${thumbnailName}` : null)
        }
        onThumbnailFile={(file) =>
          setNewThumbnail({ file, url: URL.createObjectURL(file) })
        }
        onRemoveThumbnail={
          newThumbnail ? () => setNewThumbnail(null) : undefined
        }
        galleryImages={[
          ...existingPhotos.map((ph) => ({
            id: `srv-${ph.id}`,
            url: `${FRONT_URL}/images/${ph.photoName}`,
            server: true,
            serverId: ph.id,
          })),
          ...newImages.map((i) => ({ id: i.id, url: i.url })),
        ]}
        onGalleryFiles={(files) => {
          const mapped = files.map((file) => ({
            id: Date.now() + Math.random(),
            file,
            url: URL.createObjectURL(file),
          }));
          setNewImages((prev) => [...prev, ...mapped]);
        }}
        onRemoveGalleryImage={(img) =>
          img.server
            ? handleDeleteExistingPhoto(img.serverId)
            : removeNewImage(img.id)
        }
        submitLabel="Sačuvaj izmene"
        loading={products.editLoading}
        onSubmit={handleEditProduct}
      />
    </AdminContainer>
  );
};

export default EditPage;

const PageHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  width: 100%;
  max-width: 110rem;

  h1 {
    margin-bottom: 0;
  }
`;

const BackBtn = styled.button`
  width: fit-content;
  height: fit-content;
  padding: 1.1rem 2rem;
  background: transparent;
  color: var(--muted, #8e8c94);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  cursor: pointer;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  font-size: 1.2rem;
  transition: all 0.25s var(--ease, ease);

  &:hover {
    color: var(--red, #fe5f55);
    border-color: var(--red, #fe5f55);
  }
`;
